/**
 * ToggleExclusiveSubscriptionUseCase
 *
 * Encapsulates the four-state subscribe/unsubscribe/request/cancel-request
 * branching for the "Subscribe to user" bottom sheet. Mirrors Flutter's
 * `_subscribeToUserExclusiveContent` body
 * (`lib/presentation/bottom_sheet/subscribe_user_bottom_sheet.dart:215-289`).
 *
 * Guards (matching Flutter:221-227): refuses with `false` early — without
 * calling any service — when the viewer is the target creator, or the target
 * is blocked / suspended. The Flutter `_isSubscribing` guard is *UI* state
 * and is NOT replicated here.
 *
 * Branching by `status`:
 *   - `inactive` | `cancelled`:
 *       shouldSendRequest === true  → requestSubscription(targetUserId, true)
 *       shouldSendRequest === false → subscribe(targetUserId, true)
 *   - `active`    → subscribe(targetUserId, false)
 *   - `requested` → requestSubscription(targetUserId, false)
 *
 * The data layer logs and swallows errors, returning `false` on failure, so
 * this use case does not add its own try/catch.
 */
import { ExclusiveContentSubscriptionStatus } from '@/data/enums/exclusiveSubscriptions/exclusiveContentSubscriptionStatus';
import {
  ExclusiveContentSubscriptionActionsService,
  exclusiveContentSubscriptionActionsService,
} from '@/domain/services/exclusiveContentSubscription/exclusiveContentSubscriptionActionsService';

export interface ToggleExclusiveSubscriptionInput {
  targetUserId: string;
  status: ExclusiveContentSubscriptionStatus;
  shouldSendRequest: boolean;
  currentUserId: string;
  targetUser: {
    firebaseId: string;
    blocked: boolean;
    suspended: boolean;
  };
}

export class ToggleExclusiveSubscriptionUseCase {
  private service: ExclusiveContentSubscriptionActionsService;

  constructor(service?: ExclusiveContentSubscriptionActionsService) {
    this.service = service ?? exclusiveContentSubscriptionActionsService;
  }

  async execute(input: ToggleExclusiveSubscriptionInput): Promise<boolean> {
    const { targetUserId, status, shouldSendRequest, currentUserId, targetUser } = input;

    if (
      targetUserId === currentUserId ||
      targetUser.blocked ||
      targetUser.suspended
    ) {
      return false;
    }

    switch (status) {
      case ExclusiveContentSubscriptionStatus.inactive:
      case ExclusiveContentSubscriptionStatus.cancelled:
        return shouldSendRequest
          ? this.service.requestSubscription(targetUserId, true)
          : this.service.subscribe(targetUserId, true);
      case ExclusiveContentSubscriptionStatus.active:
        return this.service.subscribe(targetUserId, false);
      case ExclusiveContentSubscriptionStatus.requested:
        return this.service.requestSubscription(targetUserId, false);
      default:
        return false;
    }
  }
}

export const createToggleExclusiveSubscriptionUseCase = (): ToggleExclusiveSubscriptionUseCase => {
  return new ToggleExclusiveSubscriptionUseCase();
};
